import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Avatar, Box, Button, Chip, Skeleton, Tooltip, Typography } from "@mui/material";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Refresh from "@mui/icons-material/Refresh";
import ManageSearch from "@mui/icons-material/ManageSearch";
import { getAiCandidateProposals } from "../../../services/MissionService";
import { resolveCandidatesForIa } from "../../../services/SearchService";
import { CircularScore, ScoreBar } from "../../Matching/components/ScoreWidgets";

const PROPOSALS_LIMIT = 10;
const MEDALS = ["#f59e0b", "#94a3b8", "#b45309"];

const extractList = (data) => {
  const list = data?.data || data?.results || data;
  return Array.isArray(list) ? list : [];
};

// AI results are keyed by the CV upload id; attach the matching CRM candidate id.
const attachCrmIds = async (list) => {
  const uploadIds = list.map((c) => c.id).filter(Boolean);
  if (!uploadIds.length) return list;
  try {
    const { data } = await resolveCandidatesForIa(uploadIds);
    const crmIdByUploadId = {};
    (Array.isArray(data) ? data : []).forEach((crm) => {
      (crm.upload_ids || [crm.upload_id]).filter(Boolean).forEach((uploadId) => {
        crmIdByUploadId[uploadId] = crm.id;
      });
    });
    return list.map((c) => ({ ...c, crmId: crmIdByUploadId[c.id] || null }));
  } catch (e) {
    return list;
  }
};

const getErrorMessage = (err) => {
  const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Erreur inconnue";
  if (typeof msg === "string" && msg.toLowerCase().includes("no job found")) {
    return "La mission n'est pas encore indexée par l'IA. Réessayez dans quelques instants.";
  }
  return `Impossible de charger les propositions IA : ${msg}`;
};

const ProposalCard = ({ candidat, index, onOpen }) => {
  const props = candidat.properties || {};
  const fullName = props.full_name && props.full_name !== "null" ? props.full_name : "";
  const parts = fullName.trim().split(" ");
  const initials = ((parts[0]?.charAt(0) || "") + (parts[1]?.charAt(0) || "")).toUpperCase() || "?";
  const score = Math.round((candidat.score ?? 0) * 100);
  const ind = candidat.individual_scores || null;
  const skills = Array.isArray(props.technical_skills) ? props.technical_skills : [];
  const subtitle = [
    props.current_title || props.title,
    props.years_of_experience > 0 ? `${props.years_of_experience} ans d'exp.` : null,
    props.location && props.location !== "not found" ? props.location : null,
  ].filter(Boolean).join(" • ");
  const medal = MEDALS[index];

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      className={`mission-view-proposal${candidat.crmId ? "" : " mission-view-proposalStatic"}`}
      title={candidat.crmId ? "Voir la fiche candidat" : "Fiche candidat introuvable dans le CRM"}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.72rem", fontWeight: 800, background: medal || "#f1f5f9", color: medal ? "#fff" : "#64748b",
        }}>
          {index + 1}
        </Box>
        <Avatar sx={{ width: 38, height: 38, bgcolor: "#ebf4fb", color: "#03407b", fontWeight: 700, fontSize: "0.85rem" }}>
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {fullName || <span style={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 500 }}>Nom non disponible</span>}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "0.76rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <CircularScore score={score} />
      </Box>

      {ind && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, mt: 1.25 }}>
          {ind.core_skills_score != null && <ScoreBar label="Compétences" score={Math.round(ind.core_skills_score * 100)} />}
          {ind.role_score != null && <ScoreBar label="Rôle" score={Math.round(ind.role_score * 100)} />}
          {ind.seniority_score != null && <ScoreBar label="Séniorité" score={Math.round(ind.seniority_score * 100)} />}
          {ind.language_score != null && <ScoreBar label="Langues" score={Math.round(ind.language_score * 100)} />}
        </Box>
      )}

      {skills.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {skills.slice(0, 5).map((skill, i) => (
            <Chip key={i} label={skill} size="small" sx={{ height: 20, fontSize: "0.68rem", color: "#475569", background: "#f1f5f9" }} />
          ))}
          {skills.length > 5 && (
            <Chip label={`+${skills.length - 5}`} size="small" sx={{ height: 20, fontSize: "0.68rem", color: "#03407b", background: "#ebf4fb", fontWeight: 700 }} />
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Shows the AI's top 10 candidates of the vivier for a mission.
 * Loads automatically when the mission view opens (including right after creation).
 */
const MissionAiProposals = ({ missionId, missionName, societeName }) => {
  const history = useHistory();
  const [candidats, setCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (isActive = () => true) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getAiCandidateProposals(missionId, PROPOSALS_LIMIT);
      const list = await attachCrmIds(extractList(data).slice(0, PROPOSALS_LIMIT));
      if (!isActive()) return;
      setCandidats(list);
    } catch (err) {
      if (!isActive()) return;
      setCandidats([]);
      setError(getErrorMessage(err));
    } finally {
      if (isActive()) setLoading(false);
    }
  }, [missionId]);

  useEffect(() => {
    let active = true;
    load(() => active);
    return () => {
      active = false;
    };
  }, [load]);

  const openCandidat = (candidat) => {
    if (candidat.crmId) history.push(`/candidats/${candidat.crmId}/view`);
  };

  // Hand over to the full matching page with this mission pre-selected.
  const openMatching = () => {
    sessionStorage.setItem("co_jobUuid", missionId);
    sessionStorage.setItem("co_mission", JSON.stringify({ id: missionId, value: missionName, nom_societe: societeName }));
    sessionStorage.setItem("co_searchText", missionName || "");
    sessionStorage.setItem("co_rawCandidats", JSON.stringify(candidats));
    history.push("/candidats-offre");
  };

  return (
    <section className="mission-view-card mission-view-proposals">
      <div className="mission-view-cardHeader">
        <span className="mission-view-step"><AutoAwesome sx={{ fontSize: 18 }} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography className="mission-view-sectionTitle">Proposition IA — Top {PROPOSALS_LIMIT} candidats</Typography>
          <Typography className="mission-view-sectionSubtitle">
            Les profils du vivier les plus adaptés à cette mission, classés par score.
          </Typography>
        </div>
        <div className="mission-view-proposalsActions">
          <Tooltip title="Relancer l'analyse IA">
            <span>
              <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={() => load()} disabled={loading}
                className="mission-view-proposalsBtn">
                Actualiser
              </Button>
            </span>
          </Tooltip>
          <Button size="small" variant="contained" startIcon={<ManageSearch />} onClick={openMatching} disabled={loading}
            className="mission-view-proposalsBtn mission-view-proposalsBtnPrimary">
            Matching détaillé
          </Button>
        </div>
      </div>

      {loading ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#03407b", fontSize: "0.85rem", fontWeight: 600, mb: 1.5 }}>
            <AutoAwesome sx={{ fontSize: 16 }} /> L'IA analyse les profils du vivier…
          </Box>
          <div className="mission-view-proposalsGrid">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={118} sx={{ borderRadius: "12px" }} />
            ))}
          </div>
        </>
      ) : error ? (
        <Alert severity="warning" sx={{ borderRadius: "10px" }}
          action={<Button color="inherit" size="small" onClick={() => load()}>Réessayer</Button>}>
          {error}
        </Alert>
      ) : candidats.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
          Aucun candidat du vivier ne correspond pour le moment à cette mission.
        </Typography>
      ) : (
        <div className="mission-view-proposalsGrid">
          {candidats.map((candidat, index) => (
            <ProposalCard
              key={candidat.id || index}
              candidat={candidat}
              index={index}
              onOpen={() => openCandidat(candidat)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default MissionAiProposals;
